import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import type { UserRole } from '@compass/client/types/membership';
import type { Organization } from '@compass/client/types/organization';
import { PhoneProvider } from '@compass/client/types/phone_number';
import type { Program } from '@compass/client/types/program';
import type { User } from '@compass/client/types/user';
import type { Workflow } from '@compass/client/types/workflow';
import { currentUser } from '@utils/user';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import type { CaseDetailsTabType } from '@/constants/caseDetails';
import type { SupportedTimeZone } from '@/constants/date';
import { SidebarStateEnum } from '@/types/sidebar';

// Normalized data structures
type OrganizationsMap = Record<string, Organization>;
type ProgramsMap = Record<string, Program>;
type WorkflowsMap = Record<string, Workflow>;
type CasesMap = Record<string, GetCaseRecordResponse>;

export interface AppState {
  // Auth State
  user: User | null;
  isAuthenticated: boolean;
  userRoles: UserRole[];

  // Organizations State - normalized with ID array
  organizations: OrganizationsMap;
  organizationIds: string[];
  selectedOrganizationId: string | null;

  // Programs State - normalized with ID array
  programs: ProgramsMap;
  programIds: string[];
  selectedProgramId: string | null;
  timeZone: SupportedTimeZone;

  // Workflows State - normalized with ID array
  workflows: WorkflowsMap;
  workflowIds: string[];
  selectedWorkflowId: string | null;

  // Cases State - normalized with ID array
  cases: CasesMap;
  caseIds: string[];
  selectedCaseId: string | null;

  // Call State
  selectedCallId: string | null;
  manuallySelectedCallId: string | null;

  /** callId -> performance.now() (monotonic ms) captured at the call's connected event.
      NOT persisted — monotonic time is meaningless across reloads. */
  connectedCallAnchors: Record<string, number>;
  setConnectedCallAnchor: (callId: string, monotonicMs: number) => void;
  clearConnectedCallAnchor: (callId: string) => void;
  /** The case's currently-live call from THIS dialpad/Twilio session, or null when no call
      is active. Mirrors the dialpad's live Twilio call so the case page can drive the Human
      Call Script tab open/close in real time (no backend refetch lag). NOT persisted — a
      Twilio call doesn't survive a reload. */
  activeCall: { callId: string; caseRecordId: string } | null;
  setActiveCall: (call: { callId: string; caseRecordId: string } | null) => void;

  /** caseRecordId -> escalation rows saved in the Human Audit "Escalate to human"
      composer. Held here (not in HumanAuditTab) because the write happens later, from
      the route-to-human-call action in DefaultCaseDetailsPage: the rows ride along as
      `action_data.audit_decision.handoff_actions` and the backend attaches them to the
      in-progress human-audit entity. NOT persisted — the reasons are free text about a
      patient's case (PHI) and must never reach localStorage (HIPAA); note `partialize`
      below is an allowlist, so omitting this keeps it in memory only. */
  stagedEscalations: Record<string, { field: string; reason: string }[]>;
  setStagedEscalations: (caseRecordId: string, rows: { field: string; reason: string }[]) => void;
  clearStagedEscalations: (caseRecordId: string) => void;

  // Layout State
  sidebarState: SidebarStateEnum;
  sidebarComponent: string;

  // Test Mode State
  testModeEnabled: boolean;

  // React Scan State (dev-only performance overlay)
  reactScanEnabled: boolean;

  // Org-wide Cases opt-in (org-wide cases list + new PQC tabs). User-controlled,
  // opt-in (default off), persisted locally. Only takes effect for eligible users
  // (permission-tier2); see useUserTier / resolveEffectiveTier.
  orgWideCasesEnabled: boolean;

  // Pagination State
  campaignPageSize: number;
  campaignDetailsPageSize: number;
  casesPageSize: number;
  qcPageSize: number;

  // Cursor Pagination State (per page)
  campaignsCurrentPage: number;
  campaignsPageCursors: Record<number, string | undefined>;
  campaignDetailsCurrentPage: number;
  campaignDetailsPageCursors: Record<number, string | undefined>;
  casesCurrentPage: number;
  casesPageCursors: Record<number, string | undefined>;
  qcCurrentPage: number;
  qcPageCursors: Record<number, string | undefined>;
  callsCurrentPage: number;
  callsPageCursors: Record<number, string | undefined>;

  // Cases Selection State
  casesSelectedIds: Set<string>;

  // Workforce Console State
  workforceSelectedTeamIds: string[];
  workforceSelectedProgramIds: string[];

  // Dialpad State
  dialpad: {
    isOpen: boolean;
    phoneNumber: string;
    caseRecordId: string | null;
    workflowName: string | null;
    workflowType: string | null; // Workflow type for disposition configuration
    callIndex: number;
    returnPath: string | null; // Path to return to when clicking external link
    caseDetailsTab: CaseDetailsTabType | null; // Tab to restore when returning
    /**
     * Org and program of the case the call was placed from, captured when the
     * dialpad opened. Stack requests use these, never the sidebar selection,
     * which the agent can change mid-call.
     */
    organizationId: string | null;
    programId: string | null;
    // UI-only state
    isMuted: boolean;
    isSpeakerOn: boolean;
    /**
     * Carrier the agent picked for the next call. Session-scoped and never
     * persisted. PHONE_PROVIDER_UNSPECIFIED means no pick, which dials "Provider 1"
     * of the order ops set in dynconfig (BV-2452) — see resolveActiveProvider.
     */
    provider: PhoneProvider;
  };

  // Actions
  setUser: (user: User | null) => void;
  setUserRoles: (roles: UserRole[]) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setOrganizations: (organizations: Organization[]) => void;
  setSelectedOrganizationId: (organizationId: string | null) => void;
  setPrograms: (programs: Program[]) => void;
  setSelectedProgramId: (programId: string | null) => void;
  setWorkflows: (workflows: Workflow[]) => void;
  setSelectedWorkflowId: (workflowId: string | null) => void;
  setCases: (cases: GetCaseRecordResponse[]) => void;
  setSelectedCaseId: (caseId: string | null) => void;
  setTimeZone: (tz: SupportedTimeZone) => void;

  // Getters for selected items
  getSelectedOrganization: () => Organization | null;
  getSelectedProgram: () => Program | null;
  getSelectedWorkflow: (workflowType?: string) => Workflow | null;
  getSelectedCase: () => GetCaseRecordResponse | null;
  getTimeZone: () => SupportedTimeZone;

  // Getters for arrays (using stored ID arrays)
  getOrganizationsArray: () => Organization[];
  getProgramsArray: () => Program[];
  getWorkflowsArray: () => Workflow[];
  getCasesArray: () => GetCaseRecordResponse[];

  // Layout Actions
  setSidebarState: (state: SidebarStateEnum) => void;
  setSidebarComponent: (component: string) => void;

  // Test Mode Actions
  setTestModeEnabled: (enabled: boolean) => void;

  // React Scan Actions
  setReactScanEnabled: (enabled: boolean) => void;

  // Org-wide Cases Actions
  setOrgWideCasesEnabled: (enabled: boolean) => void;

  // Pagination Actions
  setCampaignPageSize: (size: number) => void;
  setCampaignDetailsPageSize: (size: number) => void;
  setCasesPageSize: (size: number) => void;
  setQcPageSize: (size: number) => void;

  // Cursor Pagination Actions
  setCampaignsCurrentPage: (page: number) => void;
  setCampaignsPageCursor: (page: number, cursor: string | undefined) => void;
  resetCampaignsPagination: () => void;
  setCampaignDetailsCurrentPage: (page: number) => void;
  setCampaignDetailsPageCursor: (page: number, cursor: string | undefined) => void;
  resetCampaignDetailsPagination: () => void;
  setCasesCurrentPage: (page: number) => void;
  setCasesPageCursor: (page: number, cursor: string | undefined) => void;
  resetCasesPagination: () => void;
  setQcCurrentPage: (page: number) => void;
  setQcPageCursor: (page: number, cursor: string | undefined) => void;
  resetQcPagination: () => void;
  setCallsCurrentPage: (page: number) => void;
  setCallsPageCursor: (page: number, cursor: string | undefined) => void;
  resetCallsPagination: () => void;

  // Cases Selection Actions
  setCasesSelectedIds: (ids: Set<string>) => void;
  clearCasesSelectedIds: () => void;

  // Workforce Console Actions
  setWorkforceSelectedTeamIds: (teamIds: string[]) => void;
  setWorkforceSelectedProgramIds: (programIds: string[]) => void;
  resetWorkforceFilters: () => void;

  // Call Actions
  setSelectedCallId: (callId: string | null) => void;
  setManuallySelectedCallId: (callId: string | null) => void;
  resetSelectedCallId: () => void;

  // Dialpad Actions
  openDialpad: (
    phoneNumber?: string,
    context?: {
      caseRecordId: string;
      workflowName: string;
      workflowType?: string;
      callIndex?: number;
      returnPath?: string;
      caseDetailsTab?: CaseDetailsTabType | null;
      organizationId?: string;
      programId?: string;
    },
  ) => void;
  closeDialpad: () => void;
  setDialpadProvider: (provider: PhoneProvider) => void;
  setDialpadMuted: (muted: boolean) => void;
  setDialpadSpeakerOn: (speakerOn: boolean) => void;
  setDialpadPhoneNumber: (phoneNumber: string) => void;
  resetDialpadState: () => void;

  // Reset Method
  reset: () => void;
}

// Get initial auth state from cookies
const userData = currentUser();
const initialAuthState = userData?.user
  ? {
      user: userData?.user,
      isAuthenticated: true,
    }
  : {
      user: null,
      isAuthenticated: false,
    };

// Helper function to normalize array to object map and extract IDs
const normalizeToMapWithIds = <T extends { id?: string }>(
  items: T[],
): { map: Record<string, T>; ids: string[] } => {
  const map: Record<string, T> = {};
  const ids: string[] = [];

  items.forEach(item => {
    if (item.id) {
      map[item.id] = item;
      ids.push(item.id);
    }
  });

  return { map, ids };
};

export const useStore = create<AppState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial State from cookies
        ...initialAuthState,
        userRoles: [],
        organizations: {},
        organizationIds: [],
        selectedOrganizationId: null,
        programs: {},
        programIds: [],
        selectedProgramId: null,
        workflows: {},
        workflowIds: [],
        selectedWorkflowId: null,
        cases: {},
        caseIds: [],
        selectedCaseId: null,
        timeZone: 'ET',

        // Initial Call State
        selectedCallId: null,
        manuallySelectedCallId: null,
        connectedCallAnchors: {},
        activeCall: null,
        stagedEscalations: {},

        // Initial Layout State
        sidebarState: SidebarStateEnum.EXPANDED,
        sidebarComponent: '',

        // Initial Test Mode State
        testModeEnabled: false,

        // Initial React Scan State (on by default in dev; no-op in prod where
        // the react-scan script is never injected)
        reactScanEnabled: true,

        // Initial Org-wide Cases State (opt-in — off until the user turns it on)
        orgWideCasesEnabled: false,

        // Initial Pagination State
        campaignPageSize: 20,
        campaignDetailsPageSize: 20,
        casesPageSize: 20,
        qcPageSize: 20,

        // Initial Cursor Pagination State
        campaignsCurrentPage: 1,
        campaignsPageCursors: { 1: undefined },
        campaignDetailsCurrentPage: 1,
        campaignDetailsPageCursors: { 1: undefined },
        casesCurrentPage: 1,
        casesPageCursors: { 1: undefined },
        qcCurrentPage: 1,
        qcPageCursors: { 1: undefined },
        callsCurrentPage: 1,
        callsPageCursors: { 1: undefined },

        // Initial Cases Selection State
        casesSelectedIds: new Set<string>(),

        // Initial Workforce Console State
        workforceSelectedTeamIds: [],
        workforceSelectedProgramIds: [],

        // Initial Dialpad State
        dialpad: {
          isOpen: false,
          phoneNumber: '',
          caseRecordId: null,
          workflowName: null,
          workflowType: null,
          callIndex: 0,
          returnPath: null,
          caseDetailsTab: null,
          organizationId: null,
          programId: null,
          isMuted: false,
          isSpeakerOn: false,
          provider: PhoneProvider.PHONE_PROVIDER_UNSPECIFIED,
        },

        // Auth Actions
        setUser: user => set({ user }, false, 'auth/setUser'),
        setUserRoles: userRoles => set({ userRoles }, false, 'auth/setUserRoles'),
        setTimeZone: timeZone => set({ timeZone }),
        setIsAuthenticated: isAuthenticated =>
          set({ isAuthenticated }, false, 'auth/setIsAuthenticated'),

        // Organization Actions
        setOrganizations: organizations => {
          const { map, ids } = normalizeToMapWithIds(organizations);
          set(
            {
              organizations: map,
              organizationIds: ids,
            },
            false,
            'organizations/setOrganizations',
          );
        },
        setSelectedOrganizationId: organizationId =>
          set(
            {
              selectedOrganizationId: organizationId,
            },
            false,
            'organizations/setSelectedOrganizationId',
          ),

        // Program Actions
        setPrograms: programs => {
          const { map, ids } = normalizeToMapWithIds(programs);
          set(
            {
              programs: map,
              programIds: ids,
            },
            false,
            'programs/setPrograms',
          );
        },
        setSelectedProgramId: programId =>
          set(
            {
              selectedProgramId: programId,
            },
            false,
            'programs/setSelectedProgramId',
          ),

        // Workflow Actions
        setWorkflows: workflows => {
          const { map, ids } = normalizeToMapWithIds(workflows);
          set(
            {
              workflows: map,
              workflowIds: ids,
            },
            false,
            'workflows/setWorkflows',
          );
        },
        setSelectedWorkflowId: workflowId =>
          set(
            {
              selectedWorkflowId: workflowId,
            },
            false,
            'workflows/setSelectedWorkflowId',
          ),

        // Case Actions
        setCases: cases => {
          const { map, ids } = normalizeToMapWithIds(cases);
          set(
            {
              cases: map,
              caseIds: ids,
            },
            false,
            'cases/setCases',
          );
        },
        setSelectedCaseId: caseId =>
          set(
            {
              selectedCaseId: caseId,
            },
            false,
            'cases/setSelectedCaseId',
          ),

        // Getters for selected items
        getSelectedOrganization: () => {
          const state = get();
          return state.selectedOrganizationId
            ? state.organizations[state.selectedOrganizationId] || null
            : null;
        },
        getTimeZone: () => {
          const state = get();
          return state.timeZone;
        },
        getSelectedProgram: () => {
          const state = get();
          return state.selectedProgramId ? state.programs[state.selectedProgramId] || null : null;
        },
        getSelectedWorkflow: (workflowType?: string) => {
          const state = get();

          // If workflowType provided, find by type
          if (workflowType) {
            return (
              Object.values(state.workflows).find(
                workflow => workflow.workflow_type === workflowType,
              ) || null
            );
          }

          // Default: use selectedWorkflowId
          return state.selectedWorkflowId
            ? state.workflows[state.selectedWorkflowId] || null
            : null;
        },
        getSelectedCase: () => {
          const state = get();
          return state.selectedCaseId ? state.cases[state.selectedCaseId] || null : null;
        },

        // Getters for arrays - using stored ID arrays for performance
        getOrganizationsArray: () => {
          const state = get();
          return state.organizationIds.map(id => state.organizations[id]).filter(Boolean);
        },
        getProgramsArray: () => {
          const state = get();
          return state.programIds.map(id => state.programs[id]).filter(Boolean);
        },
        getWorkflowsArray: () => {
          const state = get();
          return state.workflowIds.map(id => state.workflows[id]).filter(Boolean);
        },
        getCasesArray: () => {
          const state = get();
          return state.caseIds.map(id => state.cases[id]).filter(Boolean);
        },

        // Layout Actions
        setSidebarState: state => set({ sidebarState: state }, false, 'layout/setSidebarState'),
        setSidebarComponent: component =>
          set({ sidebarComponent: component }, false, 'layout/setSidebarComponent'),

        // Test Mode Actions
        setTestModeEnabled: enabled =>
          set({ testModeEnabled: enabled }, false, 'testMode/setEnabled'),

        // React Scan Actions
        setReactScanEnabled: enabled =>
          set({ reactScanEnabled: enabled }, false, 'reactScan/setEnabled'),

        // Org-wide Cases Actions
        setOrgWideCasesEnabled: enabled =>
          set({ orgWideCasesEnabled: enabled }, false, 'orgWideCases/setEnabled'),

        // Pagination Actions
        setCampaignPageSize: size =>
          set({ campaignPageSize: size }, false, 'pagination/setCampaignPageSize'),
        setCampaignDetailsPageSize: size =>
          set({ campaignDetailsPageSize: size }, false, 'pagination/setCampaignDetailsPageSize'),
        setCasesPageSize: size =>
          set({ casesPageSize: size }, false, 'pagination/setCasesPageSize'),
        setQcPageSize: size => set({ qcPageSize: size }, false, 'pagination/setQcPageSize'),

        // Cursor Pagination Actions - Campaigns
        setCampaignsCurrentPage: page =>
          set({ campaignsCurrentPage: page }, false, 'pagination/setCampaignsCurrentPage'),
        setCampaignsPageCursor: (page, cursor) =>
          set(
            state => ({
              campaignsPageCursors: { ...state.campaignsPageCursors, [page]: cursor },
            }),
            false,
            'pagination/setCampaignsPageCursor',
          ),
        resetCampaignsPagination: () =>
          set(
            { campaignsCurrentPage: 1, campaignsPageCursors: { 1: undefined } },
            false,
            'pagination/resetCampaignsPagination',
          ),

        // Cursor Pagination Actions - Campaign Details
        setCampaignDetailsCurrentPage: page =>
          set(
            { campaignDetailsCurrentPage: page },
            false,
            'pagination/setCampaignDetailsCurrentPage',
          ),
        setCampaignDetailsPageCursor: (page, cursor) =>
          set(
            state => ({
              campaignDetailsPageCursors: { ...state.campaignDetailsPageCursors, [page]: cursor },
            }),
            false,
            'pagination/setCampaignDetailsPageCursor',
          ),
        resetCampaignDetailsPagination: () =>
          set(
            { campaignDetailsCurrentPage: 1, campaignDetailsPageCursors: { 1: undefined } },
            false,
            'pagination/resetCampaignDetailsPagination',
          ),

        // Cursor Pagination Actions - Cases
        setCasesCurrentPage: page =>
          set({ casesCurrentPage: page }, false, 'pagination/setCasesCurrentPage'),
        setCasesPageCursor: (page, cursor) =>
          set(
            state => ({
              casesPageCursors: { ...state.casesPageCursors, [page]: cursor },
            }),
            false,
            'pagination/setCasesPageCursor',
          ),
        resetCasesPagination: () =>
          set(
            { casesCurrentPage: 1, casesPageCursors: { 1: undefined } },
            false,
            'pagination/resetCasesPagination',
          ),

        // Cursor Pagination Actions - QC
        setQcCurrentPage: page =>
          set({ qcCurrentPage: page }, false, 'pagination/setQcCurrentPage'),
        setQcPageCursor: (page, cursor) =>
          set(
            state => ({
              qcPageCursors: { ...state.qcPageCursors, [page]: cursor },
            }),
            false,
            'pagination/setQcPageCursor',
          ),
        resetQcPagination: () =>
          set(
            { qcCurrentPage: 1, qcPageCursors: { 1: undefined } },
            false,
            'pagination/resetQcPagination',
          ),

        // Cursor Pagination Actions - Calls
        setCallsCurrentPage: page =>
          set({ callsCurrentPage: page }, false, 'pagination/setCallsCurrentPage'),
        setCallsPageCursor: (page, cursor) =>
          set(
            state => ({
              callsPageCursors: { ...state.callsPageCursors, [page]: cursor },
            }),
            false,
            'pagination/setCallsPageCursor',
          ),
        resetCallsPagination: () =>
          set(
            { callsCurrentPage: 1, callsPageCursors: { 1: undefined } },
            false,
            'pagination/resetCallsPagination',
          ),

        // Cases Selection Actions
        setCasesSelectedIds: ids =>
          set({ casesSelectedIds: ids }, false, 'cases/setCasesSelectedIds'),
        clearCasesSelectedIds: () =>
          set({ casesSelectedIds: new Set<string>() }, false, 'cases/clearCasesSelectedIds'),

        // Workforce Console Actions — switching team selection clears stale program filter.
        setWorkforceSelectedTeamIds: teamIds =>
          set(
            {
              workforceSelectedTeamIds: teamIds,
              workforceSelectedProgramIds: [],
            },
            false,
            'workforce/setSelectedTeamIds',
          ),
        setWorkforceSelectedProgramIds: programIds =>
          set(
            { workforceSelectedProgramIds: programIds },
            false,
            'workforce/setSelectedProgramIds',
          ),
        resetWorkforceFilters: () =>
          set({ workforceSelectedProgramIds: [] }, false, 'workforce/resetFilters'),

        // Call Actions
        setSelectedCallId: callId =>
          set({ selectedCallId: callId }, false, 'calls/setSelectedCallId'),

        setConnectedCallAnchor: (callId, monotonicMs) =>
          set(
            // Only one call is connected at a time, so REPLACE the map rather than
            // merge — a call that never fires 'disconnect' (crash/navigation) can't
            // then leak a stale anchor that accumulates over a long session.
            { connectedCallAnchors: { [callId]: monotonicMs } },
            false,
            'calls/setConnectedCallAnchor',
          ),
        clearConnectedCallAnchor: callId =>
          set(
            state => {
              const next = { ...state.connectedCallAnchors };
              delete next[callId];
              return { connectedCallAnchors: next };
            },
            false,
            'calls/clearConnectedCallAnchor',
          ),

        setActiveCall: call => set({ activeCall: call }, false, 'calls/setActiveCall'),

        setStagedEscalations: (caseRecordId, rows) =>
          set(
            state => ({ stagedEscalations: { ...state.stagedEscalations, [caseRecordId]: rows } }),
            false,
            'audit/setStagedEscalations',
          ),
        clearStagedEscalations: caseRecordId =>
          set(
            state => {
              if (!(caseRecordId in state.stagedEscalations)) return state;
              const next = { ...state.stagedEscalations };
              delete next[caseRecordId];
              return { stagedEscalations: next };
            },
            false,
            'audit/clearStagedEscalations',
          ),

        setManuallySelectedCallId: callId =>
          set({ manuallySelectedCallId: callId }, false, 'calls/setManuallySelectedCallId'),

        resetSelectedCallId: () =>
          set(
            { selectedCallId: null, manuallySelectedCallId: null },
            false,
            'calls/resetSelectedCallId',
          ),

        // Dialpad Actions
        openDialpad: (phoneNumber = '', context) =>
          set(
            state => ({
              dialpad: {
                ...state.dialpad,
                isOpen: true,
                phoneNumber: phoneNumber || state.dialpad.phoneNumber,
                ...(context && {
                  caseRecordId: context.caseRecordId,
                  workflowName: context.workflowName,
                  workflowType: context.workflowType ?? null,
                  callIndex: context.callIndex ?? 0,
                  returnPath: context.returnPath ?? state.dialpad.returnPath,
                  caseDetailsTab: context.caseDetailsTab ?? state.dialpad.caseDetailsTab,
                  organizationId: context.organizationId ?? null,
                  programId: context.programId ?? null,
                }),
              },
            }),
            false,
            'dialpad/open',
          ),

        closeDialpad: () =>
          set(
            state => ({
              dialpad: {
                ...state.dialpad,
                isOpen: false,
              },
            }),
            false,
            'dialpad/close',
          ),

        setDialpadProvider: provider =>
          set(
            state => ({
              dialpad: {
                ...state.dialpad,
                provider,
              },
            }),
            false,
            'setDialpadProvider',
          ),

        setDialpadMuted: muted =>
          set(
            state => ({
              dialpad: {
                ...state.dialpad,
                isMuted: muted,
              },
            }),
            false,
            'dialpad/setMuted',
          ),

        setDialpadSpeakerOn: speakerOn =>
          set(
            state => ({
              dialpad: {
                ...state.dialpad,
                isSpeakerOn: speakerOn,
              },
            }),
            false,
            'dialpad/setSpeakerOn',
          ),

        setDialpadPhoneNumber: phoneNumber =>
          set(
            state => ({
              dialpad: {
                ...state.dialpad,
                phoneNumber,
              },
            }),
            false,
            'dialpad/setPhoneNumber',
          ),

        // Resets only the per-call toggles. The stack is written against the
        // live call as it is made, so there is nothing held here to preserve.
        resetDialpadState: () =>
          set(
            state => ({
              dialpad: {
                ...state.dialpad,
                isMuted: false,
                isSpeakerOn: false,
                provider: PhoneProvider.PHONE_PROVIDER_UNSPECIFIED,
              },
            }),
            false,
            'dialpad/resetState',
          ),

        // Reset Method
        reset: () =>
          set(
            {
              userRoles: [],
              organizations: {},
              organizationIds: [],
              selectedOrganizationId: null,
              programs: {},
              programIds: [],
              selectedProgramId: null,
              workflows: {},
              workflowIds: [],
              selectedWorkflowId: null,
              cases: {},
              caseIds: [],
              selectedCaseId: null,
              qcCurrentPage: 1,
              qcPageCursors: { 1: undefined },
              selectedCallId: null,
              manuallySelectedCallId: null,
            },
            false,
            'app/reset',
          ),
      }),
      {
        name: 'compass-test-mode',
        partialize: state => ({
          testModeEnabled: state.testModeEnabled,
          reactScanEnabled: state.reactScanEnabled,
          orgWideCasesEnabled: state.orgWideCasesEnabled,
          campaignPageSize: state.campaignPageSize,
          campaignDetailsPageSize: state.campaignDetailsPageSize,
          casesPageSize: state.casesPageSize,
          qcPageSize: state.qcPageSize,
          timeZone: state.timeZone,
          workforceSelectedTeamIds: state.workforceSelectedTeamIds,
          workforceSelectedProgramIds: state.workforceSelectedProgramIds,
        }),
      },
    ),
    {
      name: '100ms|Dashboard',
    },
  ),
);
